import type { RequestOptions } from "../client";
import * as entities from "./entities";

/**
 * 3D xarita namunasi.
 *
 * Butun A korpus REYESTRDAGI shaklda: A01–A04 — 49 stellaj, A05–A10 —
 * 57; o'tish stellajlari (faqat 5–6 qavat, 4 katak) 1, 13, 26, 37, 49
 * va A06–A09 da 57-da; A10 devor yonida, o'tishsiz. Oddiy stellaj:
 * 1-qavat 4 PL05, 2-qavat 7 Y2, 3-qavat 9 Y1, 4–6 qavat 3 PL. Hududlar
 * — prod topologiyasidagi kabi (RS, PR, VZ, buferlar, Bojxona, B, C).
 *
 * Kichik namuna sahnani sinamasdi: o'tish stellajlari, qisqa va uzun
 * qatorlar, Bojxona devori faqat to'liq shaklda ko'rinadi.
 *
 * Yo'llar boshqa modullar bilan to'qnashmaydi: `/map/scene` va
 * `/work-queues/positions` yangi; `m3-` bilan boshlanadigan ID lar va
 * 3D sahnaning o'ziga xos so'rovlari (LP `limit=500`, buyurtma
 * `only_open&limit=200`) shu yerda, qolgani umumiy namunaga o'tadi.
 */

function seeded(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

function isoAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

const PASS_BAYS = new Set([1, 13, 26, 37, 49]);
const SKUS = entities.skus();

interface MockCell {
  location_id: string;
  code: string;
  zone_code: string;
  location_function: string;
  cell_type: string;
  capacity_pallets: number | null;
  occupied_slots: number;
  distinct_sku_count: number;
  on_hand_qty: string;
  is_active: boolean;
  blocked: boolean;
  sku_abc_class: string;
}

function rackCodes(): Array<{ code: string; level: number; type: string }> {
  const result: Array<{ code: string; level: number; type: string }> = [];
  for (let row = 1; row <= 10; row++) {
    const bays = row >= 5 ? 57 : 49;
    for (let bay = 1; bay <= bays; bay++) {
      const pass = row !== 10 && (PASS_BAYS.has(bay) || (bay === 57 && row >= 6));
      const levels: Array<[number, number, string]> = pass
        ? [[5, 4, "PL"], [6, 4, "PL"]]
        : [[1, 4, "PL05"], [2, 7, "Y2"], [3, 9, "Y1"], [4, 3, "PL"], [5, 3, "PL"], [6, 3, "PL"]];
      for (const [level, count, type] of levels) {
        for (let cell = 1; cell <= count; cell++) {
          const code = `A${String(row).padStart(2, "0")}-${String(bay).padStart(2, "0")}-${String(level).padStart(2, "0")}-${String(cell).padStart(2, "0")}`;
          result.push({ code, level, type });
        }
      }
    }
  }
  return result;
}

function zoneCodes(): Array<{ code: string; zone: string; fn: string }> {
  const list: Array<{ code: string; zone: string; fn: string }> = [];
  const add = (prefix: string, count: number, digits: number, zone: string, fn: string, dash = true) => {
    for (let n = 1; n <= count; n++) {
      list.push({ code: dash ? `${prefix}-${String(n).padStart(digits, "0")}` : `${prefix}${String(n).padStart(3, "0")}`, zone, fn });
    }
  };
  add("RS", 118, 3, "RS", "CONTROL");
  add("PR", 70, 2, "PR", "RECEIPT");
  add("VZ", 16, 2, "VZ", "RETURN");
  add("T", 80, 3, "T", "CUSTOMS", false);
  add("B", 523, 3, "B-STORAGE", "MX", false);
  add("C", 228, 3, "C-STORAGE", "MX", false);
  return list;
}

let sceneCache: MockCell[] | null = null;

function sceneCells(): MockCell[] {
  if (sceneCache) return sceneCache;
  const random = seeded(4242);
  const cells: MockCell[] = [];
  for (const { code, level, type } of rackCodes()) {
    const roll = random();
    const pallet = type === "PL" || type === "PL05";
    const occupied = level >= 4 ? roll < 0.58 : roll < 0.72;
    const blocked = roll > 0.994;
    cells.push({
      location_id: `m3-${code}`, code, zone_code: "A-STORAGE",
      location_function: level >= 4 ? "MX" : "OX", cell_type: type,
      capacity_pallets: pallet ? 1 : null,
      occupied_slots: occupied && pallet ? 1 : 0,
      distinct_sku_count: occupied ? 1 + Math.floor(roll * 3) : 0,
      on_hand_qty: occupied ? String(4 + Math.floor(roll * 200)) : "0",
      is_active: roll < 0.9985, blocked,
      sku_abc_class: occupied ? ["A", "B", "C"][Math.floor(roll * 3)]! : "",
    });
  }
  // Hududlar: buyurtma palletlari RS ning boshida (LP lar bilan).
  for (const { code, zone, fn } of zoneCodes()) {
    const roll = random();
    const index = Number(code.replace(/\D/g, ""));
    let occupied = 0;
    if (zone === "RS") occupied = index <= ORDER_PALLETS.length ? 1 : roll < 0.08 ? 1 : 0;
    else if (zone === "PR") occupied = roll < 0.18 ? 1 : 0;
    else if (zone === "VZ") occupied = roll < 0.2 ? 1 : 0;
    else if (zone === "T") occupied = roll < 0.25 ? 1 : 0;
    else if (zone === "B-STORAGE" || zone === "C-STORAGE") occupied = roll < 0.3 ? 1 + Math.floor(roll * 6) % 2 : 0;
    cells.push({
      location_id: `m3-${code}`, code, zone_code: zone, location_function: fn,
      cell_type: zone.endsWith("STORAGE") ? "P2" : "OP", capacity_pallets: zone.endsWith("STORAGE") ? 2 : 3,
      occupied_slots: occupied, distinct_sku_count: occupied ? 2 : 0,
      on_hand_qty: occupied ? String(20 + Math.floor(roll * 300)) : "0",
      is_active: true, blocked: false, sku_abc_class: "",
    });
  }
  sceneCache = cells;
  return cells;
}

const CUSTOMERS = ["Oila savdo MChJ", "Nur market", "Korzinka", "Makro", "Havas", "Baraka savdo", "Asl oshxona"];

/** Nazoratda (CONTROL) va ortishga tayyor (SHIPPING) buyurtma palletlari. */
const ORDER_PALLETS = Array.from({ length: 18 }, (_, index) => {
  const order = Math.floor(index / 1.5);
  return {
    lpId: `m3-lp-${index + 1}`,
    lpCode: `LP${String(10_100 + index).padStart(8, "0")}`,
    orderId: `m3-ord-${order + 1}`,
    orderNumber: `00GD-0${12_300 + order}`,
    customer: CUSTOMERS[order % CUSTOMERS.length]!,
    status: index % 5 === 4 ? "SHIPPING" : "CONTROL",
    location: `m3-RS-${String(index + 1).padStart(3, "0")}`,
  };
});

function orderLp(item: (typeof ORDER_PALLETS)[number]) {
  const random = seeded(item.lpId.length * 97 + Number(item.lpId.slice(6)));
  return {
    id: item.lpId, lp_code: item.lpCode, lp_type: "PALLET", status: item.status,
    current_location_id: item.location, warehouse_code: "A", has_stock: true, version: 3,
    created_at: isoAgo(240), updated_at: isoAgo(20),
    contents: Array.from({ length: 2 + Math.floor(random() * 3) }, (_, k) => ({
      sku_id: SKUS[(Number(item.lpId.slice(6)) * 7 + k * 11) % SKUS.length]!.id,
      qty: String(6 + Math.floor(random() * 60)),
    })),
  };
}

function orders() {
  const byId = new Map<string, ReturnType<typeof orderRow>>();
  for (const item of ORDER_PALLETS) {
    const existing = byId.get(item.orderId);
    if (existing) {
      existing.taras.push({ lp_id: item.lpId, lp_code: item.lpCode, seq: existing.taras.length + 1, current: false,
        delivered_location_id: item.location, delivered_location_code: item.location.slice(3), delivered_at: isoAgo(30) });
      continue;
    }
    byId.set(item.orderId, orderRow(item));
  }
  return [...byId.values()];
}

function orderRow(item: (typeof ORDER_PALLETS)[number]) {
  return {
    id: item.orderId, order_number: item.orderNumber, external_id: item.orderNumber,
    customer_id: `cp-${item.orderId}`, customer_name: item.customer, warehouse_code: "A",
    status: item.status === "SHIPPING" ? "HANDED_OFF" : "PICKED", priority: 0, version: 4,
    lp_id: item.lpId, lp_code: item.lpCode, created_at: isoAgo(300), picked_at: isoAgo(40),
    taras: [{ lp_id: item.lpId, lp_code: item.lpCode, seq: 1, current: true,
      delivered_location_id: item.location, delivered_location_code: item.location.slice(3), delivered_at: isoAgo(35) }],
  };
}

/** Saqlash palleti: MX katakdagi LP. */
function storedLps(locationId: string) {
  const cell = sceneCells().find((item) => item.location_id === locationId);
  if (!cell) return [];
  const pallets = ORDER_PALLETS.filter((item) => item.location === locationId).map(orderLp);
  if (pallets.length > 0) return pallets;
  if (cell.occupied_slots === 0 || cell.location_function === "OX") return [];
  const random = seeded(cell.code.length * 31 + Number(cell.code.replace(/\D/g, "").slice(-6)));
  return Array.from({ length: cell.occupied_slots }, (_, index) => ({
    id: `m3-lps-${cell.code}-${index}`, lp_code: `LP${String(Math.floor(random() * 9000) + 900).padStart(8, "0")}`,
    lp_type: "PALLET", status: cell.location_function === "MX" ? "MX" : "BUFFER",
    current_location_id: locationId, warehouse_code: "A", has_stock: true, version: 2,
    created_at: isoAgo(5000), updated_at: isoAgo(900),
    contents: [{ sku_id: SKUS[Math.floor(random() * SKUS.length)]!.id, qty: cell.on_hand_qty }],
  }));
}

interface MockWorker {
  id: string;
  name: string;
  username: string;
  roles: string[];
  kind: "forklift" | "person";
  minutes: number | null;
  step: string;
  cell?: string;
  zone?: string;
  task?: { type: string; status: string; document?: string; target_zone?: string; lines: Array<{ cell: string; status: string; planned: string; actual?: string; target?: string }> };
}

const WORKERS: MockWorker[] = [
  { id: "m3-u1", name: "Abdullajon Karimov", username: "abdullajon", roles: ["forklift_driver"], kind: "forklift", minutes: 1, step: "CONFIRM_DROP", cell: "A05-21-05-02",
    task: { type: "PALLET_LOWER", status: "IN_PROGRESS", document: "00GD-012301", lines: [{ cell: "A06-30-06-01", status: "PENDING", planned: "48", target: "VIRT-SARIQ/SARIQ" }] } },
  { id: "m3-u2", name: "Sardor Toshmatov", username: "sardor", roles: ["forklift_driver"], kind: "forklift", minutes: 3, step: "SCAN_SOURCE", cell: "A08-44-04-03",
    task: { type: "PUTAWAY", status: "IN_PROGRESS", lines: [{ cell: "PR-03", status: "CONFIRMED", planned: "60", actual: "60", target: "A08-44-04-03" }] } },
  { id: "m3-u3", name: "Bekzod Aliyev", username: "bekzod", roles: ["forklift_driver"], kind: "forklift", minutes: 4, step: "SCAN_LP", cell: "PR-12",
    // Bojxona yuki: Bojxona xonasi zal tomondan yopiq — yo'l A-4 darvozasidan
    // hovliga chiqib, A-5 eshigidan kiradi.
    task: { type: "MOVE", status: "IN_PROGRESS", document: "ПН-000731", lines: [{ cell: "PR-12", status: "PENDING", planned: "1", target: "T014" }] } },
  { id: "m3-u10", name: "Rustam Hakimov", username: "rustam", roles: ["forklift_driver"], kind: "forklift", minutes: 52, step: "CONFIRM_DROP", cell: "C040" },
  { id: "m3-u4", name: "Azamat Yusupov", username: "azamat", roles: ["picker"], kind: "person", minutes: 0.5, step: "CONFIRM_QTY", cell: "A01-08-02-03",
    task: { type: "PICK", status: "IN_PROGRESS", document: "00GD-012311", target_zone: "RS", lines: [
      { cell: "A01-05-02-01", status: "CONFIRMED", planned: "12", actual: "12" },
      { cell: "A01-08-02-03", status: "CONFIRMED", planned: "6", actual: "6" },
      { cell: "A02-17-03-04", status: "PENDING", planned: "24" },
      { cell: "A02-29-01-02", status: "PENDING", planned: "4" },
      { cell: "A04-33-03-07", status: "SHORT", planned: "10", actual: "7" },
      { cell: "A03-41-02-05", status: "PENDING", planned: "18" },
      { cell: "A06-12-01-01", status: "PENDING", planned: "8" },
    ] } },
  { id: "m3-u5", name: "Dilshod Rahimov", username: "dilshod", roles: ["picker"], kind: "person", minutes: 6, step: "SCAN_SOURCE", cell: "A07-38-03-02",
    task: { type: "PICK", status: "IN_PROGRESS", document: "НФGD-011220", target_zone: "RS", lines: [
      { cell: "A07-38-03-02", status: "PENDING", planned: "30" },
      { cell: "A09-50-02-06", status: "PENDING", planned: "12" },
      { cell: "A10-15-01-03", status: "PENDING", planned: "5" },
    ] } },
  { id: "m3-u6", name: "Fazliddin Ergashev", username: "fazliddin", roles: ["replenisher"], kind: "person", minutes: 12, step: "SCAN_TARGET", cell: "A03-20-03-04" },
  { id: "m3-u7", name: "Asatilla Qodirov", username: "asatilla", roles: ["controller"], kind: "person", minutes: 2, step: "SCAN_LP", cell: "RS-004",
    task: { type: "CONTROL", status: "IN_PROGRESS", document: "00GD-012302", lines: [{ cell: "RS-004", status: "PENDING", planned: "40" }] } },
  { id: "m3-u8", name: "Jasur Normatov", username: "jasur", roles: ["receiver"], kind: "person", minutes: 95, step: "IN_PROGRESS", zone: "PR" },
  { id: "m3-u9", name: "Olim Saidov", username: "olim", roles: ["picker"], kind: "person", minutes: null, step: "" },
];

function positions() {
  return WORKERS.map((worker) => {
    const task = worker.task
      ? { id: `m3-task-${worker.id}`, task_type: worker.task.type, status: worker.task.status, warehouse_code: "A",
          document_number: worker.task.document, target_zone_code: worker.task.target_zone, created_at: isoAgo(50) }
      : null;
    const at = worker.minutes == null ? undefined : isoAgo(worker.minutes);
    return {
      user_id: worker.id, username: worker.username, full_name: worker.name, roles: worker.roles, kind: worker.kind,
      busy: Boolean(task), current: task,
      last_activity_at: at, last_action: worker.step || undefined,
      last: at
        ? {
            at, step: worker.step, task_id: task?.id ?? "m3-task-old", task_type: worker.task?.type ?? "RECEIPT",
            task_status: worker.task?.status ?? "COMPLETED", document_number: worker.task?.document,
            scanned_code: worker.cell, location_id: worker.cell ? `m3-${worker.cell}` : undefined, zone_code: worker.zone,
          }
        : null,
    };
  });
}

function taskDetail(id: string) {
  const worker = WORKERS.find((item) => `m3-task-${item.id}` === id);
  if (!worker?.task) return undefined;
  const task = worker.task;
  return {
    id, task_type: task.type, status: task.status, warehouse_code: "A", target_zone_code: task.target_zone,
    document_number: task.document, required_role: worker.roles[0], created_at: isoAgo(50), started_at: isoAgo(30),
    lines: task.lines.map((line, index) => ({
      id: `${id}-l${index + 1}`, line_number: index + 1,
      sku_id: SKUS[(index * 13 + worker.id.length * 5) % SKUS.length]!.id,
      planned_qty: line.planned, actual_qty: line.actual, remaining_qty: line.planned,
      source_location_id: `m3-${line.cell}`, target_location_id: line.target ? `m3-${line.target}` : undefined,
      status: line.status, discrepancy_reason: line.status === "SHORT" ? "Katakda kam" : undefined,
    })),
  };
}

function page<T>(rows: T[]) {
  return { items: rows, has_more: false };
}

/** 3D xarita so'rovlari; tegishli bo'lmasa `undefined` (umumiy namunaga o'tadi). */
export function map3dRoute(path: string, options: RequestOptions): unknown {
  const query = options.query ?? {};
  if (path === "/api/v1/map/scene") {
    const columns = ["location_id", "code", "zone_code", "location_function", "cell_type", "capacity_pallets",
      "occupied_slots", "distinct_sku_count", "on_hand_qty", "is_active", "blocked", "sku_abc_class"] as const;
    return {
      columns,
      rows: sceneCells().map((cell) => columns.map((column) => cell[column])),
      truncated: false,
    };
  }
  if (path === "/api/v1/work-queues/positions") return page(positions());
  if (/^\/api\/v1\/tasks\/m3-[^/]+$/.test(path)) return taskDetail(path.split("/")[4]!);
  if (path === "/api/v1/license-plates") {
    const location = String(query.location_id ?? "");
    if (location.startsWith("m3-")) return page(storedLps(location));
    // 2D xarita katagi (umumiy namunadagi ID): bitta saqlash palleti.
    if (location) {
      const n = [...location].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
      return page([{
        id: `m3-lpx-${location}`, lp_code: `LP${String(5000 + (n % 4000)).padStart(8, "0")}`, lp_type: "PALLET",
        status: "MX", current_location_id: location, warehouse_code: "A", has_stock: true, version: 1,
        created_at: isoAgo(3000), updated_at: isoAgo(600),
        contents: [{ sku_id: SKUS[n % SKUS.length]!.id, qty: String(10 + (n % 90)) }],
      }]);
    }
    if (Number(query.limit) === 500 && (query.status === "CONTROL" || query.status === "SHIPPING")) {
      return page(ORDER_PALLETS.filter((item) => item.status === query.status).map(orderLp));
    }
    return undefined;
  }
  if (path === "/api/v1/picking/orders" && query.only_open && Number(query.limit) === 200) return page(orders());
  if (/^\/api\/v1\/picking\/orders\/m3-[^/]+$/.test(path)) {
    return orders().find((order) => order.id === path.split("/")[5]);
  }
  return undefined;
}
